import React, { useState } from "react";
import { Box, CircularProgress, Collapse, IconButton, Tooltip } from "@mui/material";
import {
  DownloadOutlined,
  NoteAddOutlined,
  ReplayOutlined,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { CurrencyHelper } from "../../../../../utils/CurrencyHelper";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import {
  SwayyCreditNote,
  SwayyInvoice,
} from "../../../../../models/SwayyInvoice/SwayyInvoice";
import {
  getSwayyCreditNoteReasonLabel,
  getSwayyInvoiceStatusLabel,
  getSwayyInvoiceStatusTone,
} from "../../../../../models/SwayyInvoice/SwayyInvoiceFilters";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";
import { userStore } from "../../../../../stores/sharedStores";
import { SwayyEmployeeRoles } from "../../../../../models/Employees/SwayyEmployeeRoles";

const StatusChip = ({
  label,
  tone = "pine",
}: {
  label: string;
  tone?: "pine" | "success" | "warning" | "danger" | "muted";
}) => {
  const styles: Record<string, { bg: string; color: string }> = {
    pine: { bg: swayyColors.pineLight, color: swayyColors.deepGreen },
    success: { bg: "rgba(30, 166, 114, 0.12)", color: swayyColors.success },
    warning: { bg: "rgba(217, 119, 6, 0.12)", color: swayyColors.warning },
    danger: { bg: "rgba(220, 38, 38, 0.1)", color: swayyColors.danger },
    muted: { bg: swayyColors.pageBg, color: swayyColors.textSecondary },
  };
  const toneStyle = styles[tone];

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.1,
        py: 0.3,
        borderRadius: swayyRadii.full,
        bgcolor: toneStyle.bg,
        color: toneStyle.color,
        fontSize: 11,
        fontWeight: 600,
        fontFamily: swayyFont,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 1.5,
      py: 0.25,
    }}
  >
    <CustomTypography
      variant="body3"
      color={swayyColors.textSecondary}
      sx={{ fontFamily: swayyFont, fontSize: 11, fontWeight: 600 }}
    >
      {label}
    </CustomTypography>
    <CustomTypography
      variant="body3"
      color={swayyColors.deepGreen}
      sx={{
        fontFamily: swayyFont,
        fontSize: 11,
        fontWeight: 600,
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {value}
    </CustomTypography>
  </Box>
);

const actionIconSx = {
  width: 30,
  height: 30,
  borderRadius: swayyRadii.sm,
  border: `1px solid ${swayyColors.border}`,
  color: swayyColors.deepGreen,
  bgcolor: swayyColors.white,
  "&:hover": {
    bgcolor: swayyColors.pineLight,
    borderColor: swayyColors.pine,
  },
};

interface SwayyShipmentInvoiceSectionProps {
  invoice: SwayyInvoice | null;
  currency?: string;
  creditNotes: SwayyCreditNote[];
  creditNotesLoading?: boolean;
  actionLoading?: boolean;
  onDownload: (url: string) => void;
  onRegenerate: (documentId: number, againstInvoiceId?: number) => void;
  onAddCreditNote: (invoiceId: number) => void;
  onLoadCreditNotes: (invoiceId: number) => void;
}

const SwayyShipmentInvoiceSection: React.FC<
  SwayyShipmentInvoiceSectionProps
> = ({
  invoice,
  currency = "INR",
  creditNotes,
  creditNotesLoading = false,
  actionLoading = false,
  onDownload,
  onRegenerate,
  onAddCreditNote,
  onLoadCreditNotes,
}) => {
  const [creditNotesOpen, setCreditNotesOpen] = useState(false);

  if (!invoice) {
    return (
      <Box
        sx={{
          mt: 1.5,
          mb: 1.5,
          p: 1.5,
          borderRadius: swayyRadii.md,
          bgcolor: swayyColors.white,
          border: `1px dashed ${swayyColors.border}`,
        }}
      >
        <CustomTypography
          variant="body3"
          color={swayyColors.textSecondary}
          sx={{
            fontFamily: swayyFont,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            mb: 0.5,
          }}
        >
          Invoice
        </CustomTypography>
        <CustomTypography
          variant="body3"
          color={swayyColors.textSecondary}
          sx={{ fontFamily: swayyFont, fontSize: 12 }}
        >
          No invoice linked to this shipment yet.
        </CustomTypography>
      </Box>
    );
  }

  const status = String(invoice.status || "").toLowerCase();
  const canEditInvoices = userStore.hasSwayyPermission(
    SwayyEmployeeRoles.EDIT_INVOICES,
  );
  const canDownload = status === "generated" && Boolean(invoice.download_url);
  const canAddCreditNote = canEditInvoices && status === "generated";
  const amountLabel = CurrencyHelper.formatCurrency(
    invoice.grand_total ?? 0,
    currency,
  );
  const issuedLabel = invoice.issued_at ? formatDate(invoice.issued_at) : "—";

  const handleToggleCreditNotes = () => {
    const nextOpen = !creditNotesOpen;
    setCreditNotesOpen(nextOpen);
    if (nextOpen) {
      onLoadCreditNotes(invoice.id);
    }
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 1.5,
        p: 1.5,
        borderRadius: swayyRadii.md,
        bgcolor: swayyColors.white,
        border: `1px solid ${swayyColors.border}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.25,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              mb: 0.75,
            }}
          >
            <CustomTypography
              variant="body3"
              color={swayyColors.textSecondary}
              sx={{
                fontFamily: swayyFont,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              Invoice
            </CustomTypography>
            <StatusChip
              label={getSwayyInvoiceStatusLabel(status)}
              tone={getSwayyInvoiceStatusTone(status)}
            />
            {status === "pending" && (
              <CustomTypography
                variant="body3"
                color={swayyColors.textSecondary}
                sx={{
                  fontFamily: swayyFont,
                  fontSize: 12,
                  fontWeight: 600,
                  fontStyle: "italic",
                }}
              >
                Generating…
              </CustomTypography>
            )}
          </Box>

          <CustomTypography
            variant="body2"
            color={swayyColors.deepGreen}
            sx={{
              fontFamily: swayyFont,
              fontWeight: 700,
              fontSize: 14,
              lineHeight: 1.35,
              wordBreak: "break-word",
            }}
          >
            {invoice.invoice_number}
          </CustomTypography>

          <CustomTypography
            variant="body3"
            color={swayyColors.textSecondary}
            sx={{
              fontFamily: swayyFont,
              fontSize: 12,
              mt: 0.5,
              lineHeight: 1.4,
            }}
          >
            {amountLabel}
            <Box component="span" sx={{ mx: 0.75, opacity: 0.45 }}>
              ·
            </Box>
            Issued {issuedLabel}
          </CustomTypography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          {canDownload && (
            <Tooltip title="Download invoice">
              <IconButton
                size="small"
                onClick={() => onDownload(invoice.download_url!)}
                sx={actionIconSx}
              >
                <DownloadOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {canEditInvoices && (status === "failed" || status === "generated") && (
            <Tooltip title="Regenerate invoice">
              <IconButton
                size="small"
                disabled={actionLoading}
                onClick={() => onRegenerate(invoice.id)}
                sx={{
                  ...actionIconSx,
                  color: swayyColors.white,
                  bgcolor: swayyColors.pine,
                  borderColor: swayyColors.pine,
                  "&:hover": { bgcolor: swayyColors.pineHover },
                }}
              >
                <ReplayOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {canAddCreditNote && (
            <Tooltip title="Add credit note">
              <IconButton
                size="small"
                onClick={() => onAddCreditNote(invoice.id)}
                sx={actionIconSx}
              >
                <NoteAddOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <CustomTypography
          variant="body3"
          color={swayyColors.pine}
          onClick={handleToggleCreditNotes}
          sx={{
            fontFamily: swayyFont,
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            userSelect: "none",
            lineHeight: 1.2,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {creditNotesOpen ? "Hide credit notes" : "See credit notes"}
        </CustomTypography>
      </Box>

      <Collapse in={creditNotesOpen} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: `1px solid ${swayyColors.border}`,
          }}
        >
          {creditNotesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 0.75 }}>
              <CircularProgress size={16} sx={{ color: swayyColors.pine }} />
            </Box>
          ) : creditNotes.length === 0 ? (
            <CustomTypography
              variant="body3"
              color={swayyColors.textSecondary}
              sx={{ fontFamily: swayyFont, fontSize: 11 }}
            >
              No credit notes yet.
            </CustomTypography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {creditNotes.map((note) => {
                const noteStatus = String(note.status || "").toLowerCase();
                const noteCanDownload =
                  noteStatus === "generated" && Boolean(note.download_url);
                return (
                  <Box
                    key={note.id}
                    sx={{
                      p: 1,
                      borderRadius: swayyRadii.sm,
                      bgcolor: swayyColors.pageBg,
                      border: `1px solid ${swayyColors.border}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                        mb: 0.25,
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color={swayyColors.deepGreen}
                        sx={{
                          fontFamily: swayyFont,
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        {note.credit_note_number}
                      </CustomTypography>
                      <StatusChip
                        label={getSwayyInvoiceStatusLabel(noteStatus)}
                        tone={getSwayyInvoiceStatusTone(noteStatus)}
                      />
                    </Box>
                    <DetailRow
                      label="Reason"
                      value={getSwayyCreditNoteReasonLabel(String(note.reason))}
                    />
                    <DetailRow
                      label="Amount"
                      value={CurrencyHelper.formatCurrency(
                        note.grand_total ?? 0,
                        currency,
                      )}
                    />
                    <DetailRow
                      label="Remarks"
                      value={note.remarks?.trim() || "—"}
                    />
                    {note.created_at && (
                      <DetailRow
                        label="Created"
                        value={formatDate(note.created_at)}
                      />
                    )}
                    <Box
                      sx={{
                        mt: 0.5,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        alignItems: "center",
                      }}
                    >
                      {noteStatus === "pending" && (
                        <CustomTypography
                          variant="body3"
                          color={swayyColors.textSecondary}
                          sx={{
                            fontFamily: swayyFont,
                            fontSize: 11,
                            fontWeight: 600,
                            fontStyle: "italic",
                          }}
                        >
                          Generating…
                        </CustomTypography>
                      )}
                      {noteCanDownload && (
                        <AppButton
                          label="Download"
                          startIcon={<DownloadOutlined sx={{ fontSize: 14 }} />}
                          onClick={() => onDownload(note.download_url!)}
                          customStyles={{
                            minHeight: 28,
                            borderRadius: swayyRadii.sm,
                            bgcolor: swayyColors.white,
                            color: swayyColors.pine,
                            border: `1px solid ${swayyColors.pine}`,
                            fontSize: 11,
                            "&:hover": { bgcolor: swayyColors.pineLight },
                          }}
                        />
                      )}
                      {canEditInvoices &&
                        (noteStatus === "failed" || noteStatus === "generated") && (
                        <AppButton
                          label="Regenerate"
                          startIcon={<ReplayOutlined sx={{ fontSize: 14 }} />}
                          onClick={() => onRegenerate(note.id, invoice.id)}
                          disabled={actionLoading}
                          customStyles={{
                            minHeight: 28,
                            borderRadius: swayyRadii.sm,
                            bgcolor: swayyColors.pine,
                            color: swayyColors.white,
                            fontSize: 11,
                            "&:hover": { bgcolor: swayyColors.pineHover },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default observer(SwayyShipmentInvoiceSection);
